/* global React, Icon, Card, Button, AiSecHead, fmt, fmt0, PlummeI18n */
// ============================================================
// AI Rådgiver — Likviditet & risiko: simulering og scenarioer
// ============================================================
// Recommended actions and scenarios share one card and one forecast curve.
// One action or several scenarios can be active at a time.

function AiiLiquidityMoves({ moves, onAct }) {
  const [open, setOpen] = React.useState(false);
  if (!moves?.length) return null;
  const openMove = (move) => {
    const intent = {
      moveAcceptedOffer: { tab: 'tilbud', checks: { akseptert: true } },
      moveFollowUpOffer: { tab: 'tilbud', checks: { sendt: true } },
      moveSendOffer: { tab: 'tilbud', checks: { opprettet: true } },
      moveOfferDrafts: { tab: 'utkast', checks: { __draft_tilbud: true } },
      moveInvoiceDrafts: { tab: 'utkast', checks: { __draft_faktura: true } },
    }[move.key];
    if (intent) {
      window.__plummeInvoiceListFilter = intent;
      window.__plummeInvoiceListTab = intent.tab;
    }
    setOpen(false);
    onAct?.(move);
  };
  const card = (move) => <article className="aii-opp aii-liquidity-move" key={move.key}>
    <span className="aii-opp-ico"><Icon name={move.icon || 'sparkle'} size={18} /></span>
    <div className="aii-opp-main">
      <div className="aii-opp-copy"><div className="aii-opp-title">{t('adv.moves.' + move.key, aiiVars(move.vars))}</div></div>
      <div className="aii-opp-value-row">
        <span className="val">{move.count != null ? t('adv.moves.count', { count: move.count }) : fmt(move.gain || 0)}</span>
        <span className="aii-value-tag">{t(move.count != null ? 'adv.moves.followup' : 'adv.moves.amount')}</span>
      </div>
      <div className="aii-opp-footer"><div className="aii-opp-meta">
        {t(move.count != null ? 'adv.moves.' + move.key + '.note' : 'adv.gain.' + move.gainNote)}
      </div></div>
      <Button size="sm" icon="sparkle" onClick={() => openMove(move)}>{t('adv.moves.go')}</Button>
    </div>
  </article>;
  return <section className="aii-liquidity-moves aii-section-frame">
    <AiSecHead icon="sparkle" title={t('adv.moves.title')} sub={t('adv.moves.liquiditySub')}
      right={moves.length > 3 ? <Button variant="ghost" size="sm" iconRight="chevRight" onClick={() => setOpen(true)}>
        {t('adv.moves.showAll', { count: moves.length })}
      </Button> : null} />
    <div className="aii-opp-grid">{moves.slice(0, 3).map(card)}</div>
    <Modal open={open} onClose={() => setOpen(false)} title={t('adv.moves.title')} max={1440} maxHeight="86vh" portal center className="aii-opp-modal"
      footer={<Button onClick={() => setOpen(false)}>{t('common.close')}</Button>}>
      <div className="aii-opp-modal-list">{moves.map(card)}</div>
    </Modal>
  </section>;
}

// ---------- «Hva skjer hvis …» ----------
// Scenarioene utfører ingenting. De regner om kurven under en forutsetning, og både
// tallene og kurven kommer fra samme beregning — teksten kan ikke si noe annet enn
// det grafen viser.
//
// De fleste scenarioene er flerlinjede: to ansatte har ulik lønn og ulik startdato,
// og en investering kan bestå av både en kontant og en lånefinansiert del. Hver linje
// redigeres for seg, og summen av dem er det kurven viser.
//
// Flere scenarioer kan stå på samtidig. Virkeligheten kommer sjelden ett spørsmål om
// gangen — man ansetter og får en ny fast kostnad i samme kvartal — så hvert aktivt
// scenario får sitt eget panel nedover, med sine egne linjer og sitt eget utslag, mens
// kurven viser dem lagt oppå hverandre.
function AiiScenarioField({ field, value, onChange }) {
  if (field.kind === 'select') {
    return <select className="input" value={value} onChange={(event) => onChange(event.target.value)}>
      {field.options.map((option) => <option key={option} value={option}>{t('adv.scn.option.' + option)}</option>)}
    </select>;
  }
  if (field.kind === 'text') {
    return <input className="input" type="text" value={value || ''} placeholder={t('adv.scn.placeholder.' + field.key)}
      onChange={(event) => onChange(event.target.value)} />;
  }
  return <input className="input" type={field.kind === 'date' ? 'date' : 'number'} value={value ?? ''}
    onChange={(event) => onChange(field.kind === 'date' ? event.target.value : Number(event.target.value) || 0)} />;
}

function AiiScenarioRow({ scenario, row, index, removable, onChange, onRemove, onOpenLoan }) {
  const isLoan = scenario.loanFields && row.financing === 'loan';
  return <div className="aii-scn-row">
    <div className="aii-scn-row-head">
      {/* Navnet brukeren har skrevet står i overskriften — ellers ville feltet vært et
          felt uten virkning noe sted. */}
      <span>{t('adv.scn.row.' + scenario.id, { n: index + 1 })}{row.costLabel ? ' · ' + row.costLabel : ''}</span>
      {removable && <button type="button" className="aii-scn-row-del" onClick={() => onRemove(index)}
        title={t('adv.scn.removeRow')} aria-label={t('adv.scn.removeRow')}>
        <Icon name="trash" size={14} />
      </button>}
    </div>
    <div className="aii-scn-fields">
      {scenario.rowFields.map((field) => <label key={field.key}>
        {t('adv.scn.field.' + field.key)}
        <AiiScenarioField field={field} value={row[field.key]} onChange={(value) => onChange(index, field.key, value)} />
      </label>)}
    </div>
    {isLoan && <button type="button" className="aii-scn-loan-btn" onClick={() => onOpenLoan(index)}>
      <Icon name="percent" size={14} />
      {t('adv.scn.loan.open')}
      <span className="aii-scn-loan-sum">{t('adv.scn.loan.summary', {
        rate: PlummeI18n.number(Number(row.interestPct) || 0),
        type: t('adv.scn.option.' + (row.loanType === 'serial' ? 'serial' : 'annuity')).toLowerCase(),
        months: Math.max(1, Number(row.termMonths) || 1),
      })}</span>
      <Icon name="chevRight" size={14} />
    </button>}
  </div>;
}

function AiiScenarioRows({ scenario, rows, onRowChange, onAddRow, onRemoveRow, onOpenLoan }) {
  return <div className="aii-scn-rows">
    {rows.map((row, index) => <AiiScenarioRow key={index} scenario={scenario} row={row} index={index}
      removable={rows.length > 1} onChange={onRowChange} onRemove={onRemoveRow} onOpenLoan={onOpenLoan} />)}
    <button type="button" className="aii-scn-add" onClick={onAddRow}>
      <Icon name="plus" size={15} />{t('adv.scn.addRow.' + scenario.id)}
    </button>
  </div>;
}

// Prosentscenarioene har ingen linjer — bare én størrelse å skru på. Slideren står
// der fordi et fast tall aldri passet: 5 % prisøkning er én bedrifts spørsmål, 60 %
// er en annens.
function AiiScenarioSlider({ scenario, value, onChange }) {
  const slider = scenario.slider;
  return <div className="aii-scn-slider">
    <div className="aii-scn-slider-head">
      <span>{t('adv.scn.slider.' + scenario.id)}</span>
      <b>{value} %</b>
    </div>
    <input className="aii-scn-range" type="range" min={slider.min} max={slider.max} step={slider.step} value={value}
      onChange={(event) => onChange('pct', Number(event.target.value) || 0)} />
    <div className="aii-scn-slider-scale"><span>{slider.min} %</span><span>{slider.max} %</span></div>
  </div>;
}

// Scenariotekstene får ferdig formatterte tall og datoer. Beregningen leverer rene
// verdier — formatet er UI-lagets ansvar, som ellers i rådgiveren.
const AII_SCENARIO_AMOUNT = /^(cost|net|extra|amount|equity|monthly|delta|interest|yearly|base)$/;
const aiiScenarioVars = (vars) => {
  const out = {};
  Object.entries(vars || {}).forEach(([key, value]) => {
    if (AII_SCENARIO_AMOUNT.test(key) && Number.isFinite(Number(value))) out[key] = fmt(Number(value));
    else if (key === 'date') out[key] = PlummeI18n.date(value, { day: 'numeric', month: 'long', year: 'numeric' });
    else out[key] = value;
  });
  return out;
};

// Visningsmodellen for de aktive scenarioene: ett oppslag per scenario brukeren har
// slått på, i den rekkefølgen de ble slått på, pluss den samlede kurven de tegner.
// Siden eier valget og linjene — her regnes bare det som skal vises av dem.
function buildAiiScenarioView({ model, scenarioIds = [], scenarioInputs = {}, cfData = [], cfStart = 0, buffer, year, months }) {
  const api = globalThis.PlummeAiAdvisorScenarios || null;
  // Prosentverdien i knappeteksten er den brukeren faktisk har dratt slideren til, ikke
  // standarden — ellers ville knappen si 5 % mens kurven viste 40 %.
  const pctFor = (scenario) => (api?.pctOf ? api.pctOf(scenario, scenarioInputs[scenario.id] || {}) : scenario.pct);
  // `tone` regnes per scenario av scenarioets egen kurve: den sier hva akkurat dette
  // utslaget gjør med bufferen, uavhengig av hva de andre gjør.
  const entries = scenarioIds.map((id) => {
    const scenario = (model?.scenarios || []).find((entry) => entry.id === id);
    if (!scenario || !api) return null;
    const inputs = scenarioInputs[id] || {};
    const result = api.computeScenario({
      scenario,
      inputs,
      context: { monthlyIncome: model.monthlyIncome, year, months,
        startIndex: year === new Date().getFullYear() ? new Date().getMonth() : 0 },
    });
    const after = result && cfData.length
      // `startIndex` er månedsnummeret vinduet begynner på. Uten det ville et scenario
      // som starter i september bli tegnet inn i vinduets første søyle.
      ? api.applyEffectToForecast({ forecast: cfData, effect: result.effect, startIndex: cfStart })
      : null;
    return {
      scenario,
      rows: scenario.repeatable ? api.rowsOf(scenario, inputs) || [] : [],
      excludedAccountCodes: inputs.excludedAccountCodes || [],
      fixedCostBase: result?.summaryVars?.base || 0,
      summary: result ? { key: result.summaryKey, vars: aiiScenarioVars(result.summaryVars) } : null,
      tone: after ? api.toneFor({ forecast: after, buffer }) : 'amber',
      effect: result?.effect || null,
    };
  }).filter(Boolean);
  // Kurven viser summen. Effektene legges sammen som flows før de blir kurve — å summere
  // ferdige kurver ville regnet med penger et gulvet scenario aldri hadde.
  const combinedEffect = api?.combineEffects ? api.combineEffects(entries.map((entry) => entry.effect)) : null;
  const after = combinedEffect && cfData.length
    ? api.applyEffectToForecast({ forecast: cfData, effect: combinedEffect, startIndex: cfStart }) || null
    : null;
  return {
    entries,
    after,
    pctFor,
    // Etiketten navngir scenarioet når det bare er ett. Med flere ville en av titlene
    // vært en halv sannhet, så da teller den dem i stedet.
    label: entries.length === 1
      ? t('adv.scn.q.' + entries[0].scenario.id, { pct: pctFor(entries[0].scenario) })
      : t('adv.cf.simScenarios', { count: entries.length }),
  };
}

// Ett panel per aktivt scenario. Panelet bærer sin egen overskrift og av-knapp, slik at
// et scenario kan skrus av der man leser det og ikke bare oppe blant chipsene.
function AiiScenarioPanel({ scenario, rows, excludedAccountCodes = [], fixedCostBase = 0, pct, tone, summary, onToggle, onInput, onRowChange, onAddRow, onRemoveRow, onOpenLoan }) {
  return <div className="aii-scn-panel">
    <div className="aii-scn-panel-head">
      <span className="aii-scn-panel-title">
        <Icon name={scenario.icon} size={16} />{t('adv.scn.q.' + scenario.id, { pct })}
      </span>
      <button type="button" className="aii-scn-panel-off" onClick={() => onToggle(scenario.id)}
        title={t('adv.scn.turnOff')} aria-label={t('adv.scn.turnOff')}>
        <Icon name="x" size={14} />
      </button>
    </div>
    {scenario.editable && scenario.repeatable && <AiiScenarioRows scenario={scenario} rows={rows || []}
      onRowChange={(index, key, value) => onRowChange(scenario.id, index, key, value)}
      onAddRow={() => onAddRow(scenario.id)}
      onRemoveRow={(index) => onRemoveRow(scenario.id, index)}
      onOpenLoan={(index) => onOpenLoan(scenario.id, index)} />}
    {scenario.id === 'reduceFixedCosts' && <div className="aii-scn-fixed-basis">
      <strong>{t('adv.scn.fixedBasis', { amount: fmt(fixedCostBase), months: scenario.fixedCostBasis.months })}</strong>
      <div>{scenario.fixedCostBasis.accounts.map((account) => <label key={account.code}>
        <input type="checkbox" checked={!excludedAccountCodes.includes(account.code)} onChange={(event) => onInput(scenario.id, 'excludedAccountCodes',
          event.target.checked ? excludedAccountCodes.filter((code) => code !== account.code) : [...excludedAccountCodes, account.code])} />
        <span>{account.code} {account.name}</span><b>{fmt(account.monthly)}</b>
      </label>)}</div>
      <p>{t('adv.scn.fixedBasisHelp')}</p>
    </div>}
    {scenario.editable && scenario.slider && <AiiScenarioSlider scenario={scenario} value={pct}
      onChange={(key, value) => onInput(scenario.id, key, value)} />}
    {summary && <div className={'aii-scn-result tone-' + (tone || 'amber')}>
      <p>{t('adv.scn.summary.' + summary.key, summary.vars)}</p>
    </div>}
  </div>;
}

// `entries` er de aktive scenarioene i den rekkefølgen brukeren slo dem på. Summen av
// dem leses av kurven og før/etter-tallene under den, ikke av en egen tekst her.
function AiiScenarioCard({ scenarios, fixedCostCountry, fixedCostEligibleYear, activeIds = [], entries = [], pctFor, onToggle, onInput, onRowChange, onAddRow, onRemoveRow,
  moves = [], selectedMove = null, onSelectMove, moveLabelFor, comparison }) {
  const [loanTarget, setLoanTarget] = React.useState(null);
  // Slås scenarioet av mens lånemodalen står åpen, ville modalen ellers blitt hengende
  // igjen over en linje som ikke finnes lenger.
  React.useEffect(() => {
    setLoanTarget((current) => (current && activeIds.includes(current.id) ? current : null));
  }, [activeIds.join('|')]);
  if (!scenarios?.length && !moves.length) return null;
  const loanEntry = loanTarget ? entries.find((entry) => entry.scenario.id === loanTarget.id) : null;
  const loanRow = loanEntry?.rows?.[loanTarget?.index] || null;
  const scenarioChip = (scenario) => <button key={scenario.id} type="button" aria-pressed={activeIds.includes(scenario.id)}
    className={'aii-scn-chip' + (activeIds.includes(scenario.id) ? ' on' : '')}
    onClick={() => onToggle(scenario.id)}>
    <Icon name={scenario.icon} size={15} />{t('adv.scn.q.' + scenario.id, { pct: pctFor(scenario) })}
  </button>;
  const costScenarios = ['reduceFixedCosts', 'hire', 'invest', 'fixedCost']
    .map((id) => (scenarios || []).find((scenario) => scenario.id === id)).filter(Boolean);
  const revenueScenarios = (scenarios || []).filter((scenario) => ['priceUp', 'revenueDown', 'loseTopCustomer'].includes(scenario.id));
  return <section className="aii-scn-section aii-section-frame">
    <div className="aii-scn-section-head"><AiSecHead icon="sparkle" title={t('adv.scn.title')} sub={t('adv.scn.sub')} help={t('adv.scn.help')} /></div>
    <Card>
    {!!moves.length && <div className="aii-scn-moves">
      <h3>{t('adv.scn.movesTitle')}</h3>
      <div className="aii-scn-chips">
        <button type="button" aria-pressed={selectedMove == null && !activeIds.length}
          className={'aii-scn-chip' + (selectedMove == null && !activeIds.length ? ' on' : '')}
          onClick={() => onSelectMove(null)}>{t('adv.cf.simBase')}</button>
        {moves.map((move, i) => <button key={move.key || i} type="button" aria-pressed={selectedMove === i}
          className={'aii-scn-chip' + (selectedMove === i ? ' on' : '')}
          onClick={() => onSelectMove(selectedMove === i ? null : i)}>
          <Icon name={move.icon || 'sparkle'} size={15} />{moveLabelFor(move)}
        </button>)}
      </div>
      <p className="t-xs muted">{t('adv.sim.note')}</p>
    </div>}
    <div className="aii-scn-choice-grid">
      <div className="aii-scn-choice-group">
        <h3>{t('adv.scn.costTitle')}</h3>
        <div className="aii-scn-chips">{costScenarios.map(scenarioChip)}</div>
        {fixedCostCountry === 'NO' && fixedCostEligibleYear && !costScenarios.some((scenario) => scenario.id === 'reduceFixedCosts') && <p className="aii-scn-no-fixed">{t('adv.scn.noFixedBasis')}</p>}
      </div>
      <div className="aii-scn-choice-group">
        <h3>{t('adv.scn.revenueTitle')}</h3>
        <div className="aii-scn-chips">{revenueScenarios.map(scenarioChip)}</div>
        {!revenueScenarios.length && <p className="t-xs muted">{t('adv.scn.noRevenueData')}</p>}
      </div>
    </div>
    {!!entries.length && <div className="aii-scn-editable">
      {entries.map((entry) => <AiiScenarioPanel key={entry.scenario.id} scenario={entry.scenario} rows={entry.rows}
        excludedAccountCodes={entry.excludedAccountCodes} fixedCostBase={entry.fixedCostBase}
        pct={pctFor(entry.scenario)} tone={entry.tone} summary={entry.summary} onToggle={onToggle} onInput={onInput}
        onRowChange={onRowChange} onAddRow={onAddRow} onRemoveRow={onRemoveRow}
        onOpenLoan={(id, index) => setLoanTarget({ id, index })} />)}
    </div>}
    {comparison && <div className="aii-scn-comparison" aria-live="polite">
      <div><span>{t('adv.scn.endBalance')}</span><b>{fmt(comparison.baseEnd)} → {fmt(comparison.afterEnd)}</b></div>
      <div><span>{t('adv.scn.lowBalance')}</span><b>{fmt(comparison.baseLow)} → {fmt(comparison.afterLow)}</b></div>
      {comparison.baseBelowBuffer != null && <div><span>{t('adv.scn.monthsBelowBuffer')}</span>
        <b>{comparison.baseBelowBuffer} → {comparison.afterBelowBuffer}</b></div>}
      <div><span>{t('adv.cf.simDiff')}</span><b>{comparison.delta > 0 ? '+' : comparison.delta < 0 ? '−' : ''}{fmt(Math.abs(comparison.delta))}</b></div>
    </div>}
    <p className="t-xs muted" style={{ marginTop: 10 }}>{t('adv.scn.note')}</p>
    {window.AiiLoanModal && <window.AiiLoanModal open={!!loanRow} row={loanRow} rowNumber={(loanTarget?.index || 0) + 1}
      onChange={(key, value) => loanTarget && onRowChange(loanTarget.id, loanTarget.index, key, value)} onClose={() => setLoanTarget(null)} />}
    </Card>
  </section>;
}

Object.assign(window, { AiiLiquidityMoves, AiiScenarioCard, buildAiiScenarioView });
